import { FC, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import { Reveal, ThemeObject, themes } from '@evervault/react'
import { toast } from 'sonner'
import './group-card.css'


// COMPONENTS
import * as Icons from '@icons'
import { Skeleton } from '@/components/shadcn/skeleton'
import PaymentMethodBrand from '@components/atoms/payment-method-brand'

// UTILITIES
import { EvervaultAPI } from '@apis'
import { parseUserLimits } from '@schemas'
import { flatten } from '@utils/string-helper'
import { priceFormatter } from '@utils/number-helper'
import { getGroupStatus } from '@utils/temp-helpers'
import { hapticsImpactMedium, platform } from '@utils/capacitor-helper'
import { isDemoGroup } from '@/routes/group/[groupId]/demo-data'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
    group: Group,
    userPledges: Pledge[],
    onConfirmCancel?: () => void,
    onCardReady?: () => void,
    hideAddMoney?: boolean
}

const theme = themes.clean({
    styles: {
        '#root > div': {
            'font-size': '1.25rem',
            'font-family': 'inherit',
            'font-weight': 'bold',
            'color': 'white',
        },
        'button': {
            'transition': 'all 0.3s ease',
            'border-radius': '5px',
            'border': '1px solid transparent',
            'font-family': 'inherit',
            'font-size': 'inherit',
            'font-weight': 'bold',
            'cursor': 'pointer',
            'border-style': 'solid',
            'position': 'relative',
            'text-shadow': 'none',
            'outline': 'none',
            'border-width': '1px',
            '-webkit-appearance': 'button',
            'appearance': 'button',
            'padding': '0.8em 1em',
            'border-color': 'rgb(0, 0, 0)',
            'background': 'rgb(255, 255, 255)',
            'color': 'rgb(0, 0, 0)',
            'box-sizing': 'border-box',

            'height': '100vh',
            'width': '100vw',
        }
    }
})

const defaultCopyTheme = themes.clean({
    styles: {
        'button': {
            'background-color': 'rgba(255, 255, 255, 0.1)',
            'background-image': 'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE0IDEySDhDNi44OTY4NyAxMiA2IDExLjEwMzEgNiAxMFYyQzYgMC44OTY4NzUgNi44OTY4NyAwIDggMEgxMi4zNzgxQzEyLjc3NSAwIDEzLjE1NjIgMC4xNTkzNzUgMTMuNDM3NSAwLjQ0MDYyNUwxNS41NTk0IDIuNTYyNUMxNS44NDA2IDIuODQzNzUgMTYgMy4yMjUgMTYgMy42MjE4OFYxMEMxNiAxMS4xMDMxIDE1LjEwMzEgMTIgMTQgMTJaTTIgNEg1VjUuNUgyQzEuNzI1IDUuNSAxLjUgNS43MjUgMS41IDZWMTRDMS41IDE0LjI3NSAxLjcyNSAxNC41IDIgMTQuNUg4QzguMjc1IDE0LjUgOC41IDE0LjI3NSA4LjUgMTRWMTNIMTBWMTRDMTAgMTUuMTAzMSA5LjEwMzEyIDE2IDggMTZIMkMwLjg5Njg3NSAxNiAwIDE1LjEwMzEgMCAxNFY2QzAgNC44OTY4NyAwLjg5Njg3NSA0IDIgNFoiIGZpbGw9IiNDN0RCRkYiLz4KPC9zdmc+Cg==)',
            'background-size': '1rem',
            'background-position': '1rem center',
            'background-repeat': 'no-repeat',
            'height': '2.5rem',
            'width': '100%',
            'border': '1px solid rgba(255, 255, 255, 0.2)',
            'border-radius': '8px',
            'cursor': 'pointer',
            'color': '#C7DBFF',
            'font-weight': '600',
            'font-size': '0.875rem',
            'padding-left': '2.5rem',
            'padding-right': '1rem',
            'transition': 'all 0.2s ease',
        },
        'button:hover': {
            'background-color': 'rgba(255, 255, 255, 0.15)',
        }
    }
})

const successCopyTheme = themes.clean({
    styles: {
        'button': {
            'background-color': 'rgba(74, 222, 128, 0.1)',
            'background-image': 'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE0IDEySDhDNi44OTY4NyAxMiA2IDExLjEwMzEgNiAxMFYyQzYgMC44OTY4NzUgNi44OTY4NyAwIDggMEgxMi4zNzgxQzEyLjc3NSAwIDEzLjE1NjIgMC4xNTkzNzUgMTMuNDM3NSAwLjQ0MDYyNUwxNS41NTk0IDIuNTYyNUMxNS44NDA2IDIuODQzNzUgMTYgMy4yMjUgMTYgMy42MjE4OFYxMEMxNiAxMS4xMDMxIDE1LjEwMzEgMTIgMTQgMTJaTTIgNEg1VjUuNUgyQzEuNzI1IDUuNSAxLjUgNS43MjUgMS41IDZWMTRDMS41IDE0LjI3NSAxLjcyNSAxNC41IDIgMTQuNUg4QzguMjc1IDE0LjUgOC41IDE0LjI3NSA4LjUgMTRWMTNIMTBWMTRDMTAgMTUuMTAzMSA5LjEwMzEyIDE2IDggMTZIMkMwLjg5Njg3NSAxNiAwIDE1LjEwMzEgMCAxNFY2QzAgNC44OTY4NyAwLjg5Njg3NSA0IDIgNFoiIGZpbGw9IiM0QURFODAiLz4KPC9zdmc+Cg==)',
            'background-size': '1rem',
            'background-position': '1rem center',
            'background-repeat': 'no-repeat',
            'height': '2.5rem',
            'width': '100%',
            'border': '1px solid #4ADE80',
            'border-radius': '8px',
            'cursor': 'default',
            'color': '#4ADE80',
            'font-weight': '700',
            'font-size': '0.875rem',
            'padding-left': '2.125rem',
            'padding-right': '1rem',
            'transition': 'all 0.2s ease',
            'text-transform': 'uppercase',
        }
    }
})

const compactCopyTheme = themes.clean({
    styles: {
        'button': {
            'background-color': 'rgba(255, 255, 255, 0.1)',
            'background-image': 'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE0IDEySDhDNi44OTY4NyAxMiA2IDExLjEwMzEgNiAxMFYyQzYgMC44OTY4NzUgNi44OTY4NyAwIDggMEgxMi4zNzgxQzEyLjc3NSAwIDEzLjE1NjIgMC4xNTkzNzUgMTMuNDM3NSAwLjQ0MDYyNUwxNS41NTk0IDIuNTYyNUMxNS44NDA2IDIuODQzNzUgMTYgMy4yMjUgMTYgMy42MjE4OFYxMEMxNiAxMS4xMDMxIDE1LjEwMzEgMTIgMTQgMTJaTTIgNEg1VjUuNUgyQzEuNzI1IDUuNSAxLjUgNS43MjUgMS41IDZWMTRDMS41IDE0LjI3NSAxLjcyNSAxNC41IDIgMTQuNUg4QzguMjc1IDE0LjUgOC41IDE0LjI3NSA4LjUgMTRWMTNIMTBWMTRDMTAgMTUuMTAzMSA5LjEwMzEyIDE2IDggMTZIMkMwLjg5Njg3NSAxNiAwIDE1LjEwMzEgMCAxNFY2QzAgNC44OTY4NyAwLjg5Njg3NSA0IDIgNFoiIGZpbGw9IiNDN0RCRkYiLz4KPC9zdmc+Cg==)',
            'background-size': '1rem',
            'background-position': 'center',
            'background-repeat': 'no-repeat',
            'height': '2.5rem',
            'width': '2.5rem',
            'min-width': '2.5rem',
            'border': '1px solid rgba(255, 255, 255, 0.2)',
            'border-radius': '8px',
            'cursor': 'pointer',
            'color': 'transparent',
            'font-size': '0',
            'padding': '0',
            'overflow': 'hidden',
            'transition': 'all 0.2s ease',
        },
        'button:hover': {
            'background-color': 'rgba(255, 255, 255, 0.15)',
        }
    }
})

const compactSuccessCopyTheme = themes.clean({
    styles: {
        'button': {
            'background-color': 'rgba(74, 222, 128, 0.1)',
            'background-image': 'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE0IDEySDhDNi44OTY4NyAxMiA2IDExLjEwMzEgNiAxMFYyQzYgMC44OTY4NzUgNi44OTY4NyAwIDggMEgxMi4zNzgxQzEyLjc3NSAwIDEzLjE1NjIgMC4xNTkzNzUgMTMuNDM3NSAwLjQ0MDYyNUwxNS41NTk0IDIuNTYyNUMxNS44NDA2IDIuODQzNzUgMTYgMy4yMjUgMTYgMy42MjE4OFYxMEMxNiAxMS4xMDMxIDE1LjEwMzEgMTIgMTQgMTJaTTIgNEg1VjUuNUgyQzEuNzI1IDUuNSAxLjUgNS43MjUgMS41IDZWMTRDMS41IDE0LjI3NSAxLjcyNSAxNC41IDIgMTQuNUg4QzguMjc1IDE0LjUgOC41IDE0LjI3NSA4LjUgMTRWMTNIMTBWMTRDMTAgMTUuMTAzMSA5LjEwMzEyIDE2IDggMTZIMkMwLjg5Njg3NSAxNiAwIDE1LjEwMzEgMCAxNFY2QzAgNC44OTY4NyAwLjg5Njg3NSA0IDIgNFoiIGZpbGw9IiM0QURFODAiLz4KPC9zdmc+Cg==)',
            'background-size': '1rem',
            'background-position': 'center',
            'background-repeat': 'no-repeat',
            'height': '2.5rem',
            'width': '2.5rem',
            'min-width': '2.5rem',
            'border': '1px solid #4ADE80',
            'border-radius': '8px',
            'cursor': 'default',
            'color': 'transparent',
            'font-size': '0',
            'padding': '0',
            'overflow': 'hidden',
            'transition': 'all 0.2s ease',
        }
    }
})

const GroupCard: FC<Props> = (props) => {
    const { className: classProps, group, userPledges, onConfirmCancel, onCardReady, hideAddMoney, ...divProps } = props
    const { user, isLoaded: isClerkLoaded } = useUser()
    const { groupStatus } = getGroupStatus(group)
    const navigate = useNavigate()
    const userLimits = useMemo(() => parseUserLimits(user?.publicMetadata?.limits), [user?.publicMetadata?.limits])
    const [{ loading, data, err }, setData] = useState<{ loading: boolean, data?: EvervaultAPI.GetCardRequest, err?: any }>({ loading: true })
    const [cardDetailsReady, setCardDetailsReady] = useState<boolean>(false)
    const [isCopied, setIsCopied] = useState(false)
    const [isRevealed, setIsRevealed] = useState(false)
    const className = flatten("group-card", classProps)
    // Name on Card
    const NoC = (group.type === 'merchant')
        ? `${group.merchant?.user?.givenName || ''} ${group.merchant?.user?.familyName || ''}`.trim()
        : !!group.organizer
            ? `${group.organizer.givenName || ''} ${group.organizer.familyName || ''}`.trim()
            : ''

    // Check if this is a physical card or linked virtual card (can show data immediately without Evervault)
    const isPhysicalCard = group.card?.cardType === 'physical'
    const isLinkedVirtualCard = isClerkLoaded && group.card?.id === user?.publicMetadata.tera?.virtualCard?.id
    const canShowCardData = isPhysicalCard || isLinkedVirtualCard

    const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') getCardDetails()
    }

    useLayoutEffect(() => {
        document.addEventListener('visibilitychange', onVisibilityChange)

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange)
        }
    })

    // Generate get card details request to pass to Evervault
    // Wait for Clerk to be loaded before fetching card details
    useEffect(() => {
        if (isClerkLoaded) {
            getCardDetails()
        }
    }, [group.card?.id, isClerkLoaded])

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 450)
    const [isMobileXS, setIsMobileXS] = useState(window.innerWidth <= 400)
    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth
            setIsMobile(width <= 450)
            setIsMobileXS(width <= 400)
        }
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    function getCardDetails() {
        if (!!group.card?.id) {
            setData({ loading: true })
            // ToDo: Setup AbortionController for cardId changes
            EvervaultAPI.GetCardRequest.init(group.card.id, { headers: { "Access-Control-Allow-Methods": "POST, GET", "Access-Control-Allow-Origin": "https://ui-components.evervault.com" } })
                .then((request) => setData({ loading: false, data: request }))
                .catch((err: unknown) => {
                    console.error(err)
                    setData({ loading: false, err })
                })
        }
    }

    function handleCopy() {
        if (platform !== 'web') hapticsImpactMedium()
        toast.success('Card number copied')
        setIsCopied(true)
        setTimeout(() => setIsCopied(false), 2000)
    }

    // Demo mode: render static card without Evervault
    if (isDemoGroup(group.id)) {
        return (
            <div className={flatten("group-card", classProps)} {...divProps}>
                <div className='flex flex-row items-start justify-between'>
                    <div className='flexcolumn gap-2'>
                        <div className='flex flex-row items-center gap-2'>
                            <img className='splitcard' src="/splitcard/logo/white-and-blue-fallback.png" alt="Splitcard Logo" />
                        </div>
                    </div>
                    {groupStatus === 'active' && <div className='flex flex-col items-end gap-2'>
                        <p className="text-body-2 select-none text-gray-500">Available</p>
                        <h1 style={{ fontSize: '2rem' }}>{priceFormatter.format(((group.collectedAmount || 0) - (group.spendAmount || 0)) / 100)}</h1>
                    </div>}
                </div>
                <div className="card-number-container flex flex-row items-center" style={{ margin: groupStatus !== 'active' ? '1.5rem 0' : '0' }}>
                    <div className="card-mask">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="card-mask-group">
                                <div className="card-mask-dot" />
                                <div className="card-mask-dot" />
                                <div className="card-mask-dot" />
                                <div className="card-mask-dot" />
                            </div>
                        ))}
                        <p className="text-body select-none" style={{ fontSize: '1.25rem', marginLeft: '0.25rem' }}>{group.card?.lastFour}</p>
                    </div>
                    <div className="copy-button-wrapper">
                        <button className={flatten("reveal", isMobileXS ? "reveal-icon-only" : "")} disabled style={{ opacity: 0.5, cursor: 'default' }}>
                            <div className="reveal-icon" style={isMobileXS ? { position: 'static', display: 'flex' } : { position: 'absolute', left: '1rem', display: 'flex' }}>
                                <Icons.View state="on" tint="#C7DBFF" width="1rem" height="1rem" />
                            </div>
                            {!isMobileXS && 'Reveal'}
                        </button>
                    </div>
                </div>
                <div className="flex flex-row items-end justify-between">
                    <div className="flex flex-col">
                        {!!NoC && <h2 style={{ fontSize: '1.1rem' }}>{NoC}</h2>}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                        <div className="flex flex-row items-center" style={{ gap: '1.5rem' }}>
                            <p className="select-none">{group.card?.expirationMonth}/{group.card?.expirationYear?.slice(-2)}</p>
                            <p className="select-none">&bull;&bull;&bull;</p>
                        </div>
                        <PaymentMethodBrand className='mastercard-icon' height='2rem' width='3rem' brand="mastercard-icon" draggable="false" />
                    </div>
                </div>
            </div>
        )
    }

    // Show a loading card with skeletons while data is loading
    if (loading || err || !data) {
        return (
            <div className={flatten("group-card", classProps)} {...divProps}>
                <div className='flex flex-row items-start justify-between'>
                    <div className='flexcolumn gap-2'>
                        <div className='flex flex-row items-center gap-2'>
                            <img className='splitcard' src="/splitcard/logo/white-and-blue-fallback.png" alt="Splitcard Logo" />
                        </div>
                    </div>
                    {groupStatus === 'active' && <div className='flex flex-col items-end gap-2'>
                        <p className="text-body-2 select-none text-gray-500">Available</p>
                        <h1 style={{ fontSize: '2rem' }}>{priceFormatter.format(((group.collectedAmount || 0) - (group.spendAmount || 0)) / 100)}</h1>
                    </div>}
                </div>
                <div className="card-number-container flex flex-row items-center" style={{ margin: groupStatus !== 'active' ? '1.5rem 0' : '0' }}>
                    <div className="card-mask">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="card-mask-group">
                                <div className="card-mask-dot" />
                                <div className="card-mask-dot" />
                                <div className="card-mask-dot" />
                                <div className="card-mask-dot" />
                            </div>
                        ))}
                        {canShowCardData && group.card?.lastFour ? (
                            <p className="text-body select-none" style={{ fontSize: '1.5rem', marginLeft: '0.25rem' }}>{group.card.lastFour}</p>
                        ) : (
                            <Skeleton style={{ width: '3rem', height: '1.5rem', marginLeft: '0.25rem' }} />
                        )}
                    </div>
                    <div className="copy-button-wrapper">
                        <Skeleton style={{ width: isMobileXS ? '2.5rem' : '7rem', height: '2.5rem' }} />
                    </div>
                </div>
                <div className="flex flex-row items-end justify-between">
                    <div className="flex flex-col">
                        {!!NoC && <h2 style={{ fontSize: '1.1rem' }}>{NoC}</h2>}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                        <div className="flex flex-row items-center" style={{ gap: '1.5rem' }}>
                            {canShowCardData && group.card?.expirationMonth && group.card?.expirationYear ? (
                                <p className="select-none">{group.card.expirationMonth}/{group.card.expirationYear.slice(-2)}</p>
                            ) : (
                                <div className="flex flex-row items-center gap-1">
                                    <Skeleton style={{ width: '1.5rem', height: '1rem' }} />
                                    <p className="select-none">/</p>
                                    <Skeleton style={{ width: '1.5rem', height: '1rem' }} />
                                </div>
                            )}
                            <Skeleton style={{ width: '2rem', height: '1rem' }} />
                        </div>
                        <PaymentMethodBrand className='mastercard-icon' height='2rem' width='3rem' brand="mastercard-icon" draggable="false" />
                    </div>
                </div>
            </div>
        )
    }

    return (<>
        <div>
            <Reveal request={data!} onReady={() => { setCardDetailsReady(true); onCardReady?.() }}>
                <div className={className} {...divProps}>
                    <div className='flex flex-row items-start justify-between'>
                        <div className='flexcolumn gap-2'>
                            <div className='flex flex-row items-center gap-2'>
                                <img className='splitcard' src="/splitcard/logo/white-and-blue-fallback.png" alt="Splitcard Logo" />
                                <p className="text-body highlight" style={{ visibility: isRevealed ? 'visible' : 'hidden' }}>Banking Partners:</p>
                            </div>
                            <div className='flex flex-row items-center gap-2' style={{ visibility: isRevealed ? 'visible' : 'hidden' }}>
                                <img className="corpay select-none" src="/corpay-logo.svg" draggable="false" />
                                <p>&</p>
                                <img className="tera select-none" src="/tera/name.svg" draggable="false" />
                            </div>
                        </div>
                        {groupStatus === 'active' && <div className='flex flex-col items-end gap-2'>
                            <p className="text-body-2 select-none text-gray-500">Available</p>
                            <h1 style={{ fontSize: '2rem' }}>{priceFormatter.format(((group.collectedAmount || 0) - (group.spendAmount || 0)) / 100)}</h1>
                            {!hideAddMoney && userLimits.addAdditionalFunds && <div className="flex flex-row items-center justify-center add-money highlight" onClick={() => navigate(`./add/${userPledges[0].id}`)}>
                                <div className='icon'><Icons.Plus width="1rem" height="1rem" /></div>
                                <div className='text-center'><p className='text-body-2'><strong>Add Money</strong></p></div>
                            </div>}
                        </div>}
                    </div>
                    <div className="card-number-container flex flex-row items-center" style={{ margin: groupStatus !== 'active' ? '1.5rem 0' : '0' }}>
                        {!isRevealed ? (
                            <div className="card-mask">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="card-mask-group">
                                        <div className="card-mask-dot" />
                                        <div className="card-mask-dot" />
                                        <div className="card-mask-dot" />
                                        <div className="card-mask-dot" />
                                    </div>
                                ))}
                                {(cardDetailsReady || canShowCardData)
                                    ? <p className="text-body select-none evervault" style={{ fontSize: '1.25rem', marginLeft: '0.25rem' }}>{group.card?.lastFour}</p>
                                    : <Skeleton style={{ width: '3rem', height: '1.5rem', marginLeft: '0.25rem' }} />
                                }
                            </div>
                        ) : (
                            <ProxyText placeholder="XXXX XXXX XXXX XXXX " fontSize={isMobile ? '1.2rem' : '1.5rem'} fontWeight="200" path="$.card.cardNumber" format={{ regex: /(.{4})/g, replace: '$1 ' }} minWidth="auto" style={{ width: 'auto', whiteSpace: 'nowrap' }} />
                        )}
                        <div className="copy-button-wrapper">
                            {!cardDetailsReady ? (
                                <Skeleton style={{ width: isMobileXS ? '2.5rem' : '7rem', height: '2.5rem' }} />
                            ) : isRevealed ? (
                                // @ts-ignore
                                <Reveal.CopyButton theme={isMobileXS ? (isCopied ? compactSuccessCopyTheme : compactCopyTheme) : (isCopied ? successCopyTheme : defaultCopyTheme)}
                                    key={isCopied ? 'copied' : 'default'}
                                    path="$.card.cardNumber"
                                    text={isCopied ? "COPIED" : "Copy"}
                                    onCopy={handleCopy}
                                />
                            ) : (
                                <button className={flatten("reveal", isMobileXS ? "reveal-icon-only" : "")} onClick={() => setIsRevealed(true)}>
                                    <div className="reveal-icon" style={isMobileXS ? { position: 'static', display: 'flex' } : { position: 'absolute', left: '1rem', display: 'flex' }}>
                                        <Icons.View state="on" tint="#C7DBFF" width="1rem" height="1rem" />
                                    </div>
                                    {!isMobileXS && 'Reveal'}
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-row items-end justify-between">
                        <div className="flex flex-col">
                            {!!NoC && <h2 style={{ fontSize: '1.1rem' }}>{NoC}</h2>}
                            {(groupStatus === 'active' && group.card?.cardType === 'physical') && <p className="text-body-2">Linked to your physical card</p>}
                            {(groupStatus === 'active' && (group.card?.id === user?.publicMetadata.tera?.virtualCard?.id)) && <p className="text-body-2">Linked to your reloadable card</p>}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                            <div className="flex flex-row items-center" style={{ gap: '1.5rem' }}>
                                <div className="flex flex-row items-center evervault gap-1">
                                    {(cardDetailsReady || canShowCardData)
                                        ? <p className="select-none">{group.card?.expirationMonth}</p>
                                        : <Skeleton style={{ width: '1.5rem', height: '1rem' }} />
                                    }
                                    <p className="select-none">/</p>
                                    {(cardDetailsReady || canShowCardData)
                                        ? <p className="select-none">{group.card?.expirationYear?.slice(-2)}</p>
                                        : <Skeleton style={{ width: '1.5rem', height: '1rem' }} />
                                    }
                                </div>
                                {cardDetailsReady
                                    ? <ProxyText placeholder="CVV" path="$.card.cardSecurityCode" fontWeight="200" />
                                    : <Skeleton style={{ width: '2rem', height: '1rem' }} />
                                }
                            </div>
                            <PaymentMethodBrand className='mastercard-icon' height='2rem' width='3rem' brand="mastercard-icon" draggable="false" />
                        </div>
                    </div>
                </div>
            </Reveal >
        </div >
    </>)
}

interface ProxyTextProps extends React.HTMLAttributes<HTMLDivElement> {
    placeholder?: string,
    path: string,
    format?: { regex: RegExp, replace: string, },
    color?: string,
    minWidth?: string,
    fontSize?: string,
    fontWeight?: string
}

const ProxyText: FC<ProxyTextProps> = (props) => {
    const { className: classProps, style: styleProps, placeholder = '', path, format, color = 'white', minWidth, fontSize, fontWeight, ...divProps } = props
    const className = flatten("proxy-text-container", classProps)
    const width = `${placeholder.length < 2.4 ? 2.4 : placeholder.length + .75}ch`

    return (
        <div className={className} style={{ width, minWidth: minWidth ? minWidth : width, ...styleProps }} {...divProps}>
            <Reveal.Text theme={{
                ...theme,
                styles: {
                    ...(theme as ThemeObject).styles,
                    '#root > div': {
                        ...((theme as ThemeObject).styles?.['#root > div'] as object),
                        'font-family': 'inherit',
                        'color': color,
                        'text-align': color === 'black' ? 'right' : 'left',
                        'white-space': 'nowrap',
                        ...(fontSize ? { 'font-size': fontSize } : {}),
                        ...(fontWeight ? { 'font-weight': fontWeight } : {})
                    },
                }
            }} path={path} format={format} />
        </div>
    )
}

export default GroupCard
